// Capability catalog for Tensor Hive's no-code Agent Builder.
//
// Each capability maps to an underlying model. `available` reflects whether
// that model actually works in THIS project's environment today — native
// Cloudflare Workers AI models (the `@cf/...` models) are currently rejected
// with 401 "ai gateway request rejected" by the platform's AI gateway here
// (verified via a one-off probe on 2026-07-21), so those stay unavailable.
// The Claude chat-compat path (text + vision) works, and speechToText /
// imageGeneration / videoGeneration / webSearch are unblocked separately via
// the owner's own third-party API keys (Deepgram, deapi.ai, Serper.dev)
// called directly from the backend — see
// partyserver/src/utils/{deepgram,deapi,serper}.ts.
//
// IMPORTANT: this file is mirrored (kept in sync manually) at
// partyserver/src/utils/agentCapabilities.ts for backend enforcement, since
// the frontend and backend bundles can't share a module directly.

export type AgentCapabilityCategory = 'Core' | 'Vision' | 'Language' | 'Image' | 'Audio';

export interface AgentCapability {
  id: string;
  label: string;
  description: string;
  category: AgentCapabilityCategory;
  /** The underlying model this capability maps to. */
  model: string;
  /** Whether this capability actually works in this project's environment today. */
  available: boolean;
}

export const AGENT_CAPABILITIES: AgentCapability[] = [
  {
    id: 'textChat',
    label: 'Text Chat',
    description: "Core conversational ability — every agent can chat. Always on.",
    category: 'Core',
    model: 'anthropic/claude-sonnet-4-6',
    available: true,
  },
  {
    id: 'visionUnderstanding',
    label: 'Vision (chat with images)',
    description: 'Understands and discusses images the renter attaches during chat.',
    category: 'Vision',
    model: 'anthropic/claude-sonnet-4-6 (vision, compat)',
    available: true,
  },
  {
    id: 'ocr',
    label: 'OCR / Read text from images',
    description: 'Reads and extracts visible text from an attached image.',
    category: 'Vision',
    model: 'anthropic/claude-sonnet-4-6 (vision, compat)',
    available: true,
  },
  {
    id: 'webSearch',
    label: 'Web Search',
    description: 'Searches the web (Google, via Serper) for current information to answer questions.',
    category: 'Language',
    model: 'Serper.dev Google Search API',
    available: true,
  },
  {
    id: 'embeddings',
    label: 'Embeddings (semantic search)',
    description: 'Generates vector embeddings for semantic search over text.',
    category: 'Language',
    model: '@cf/baai/bge-base-en-v1.5',
    available: false,
  },
  {
    id: 'imageCaption',
    label: 'Image-to-Text / Captioning',
    description: 'Generates a short caption describing an image.',
    category: 'Vision',
    model: '@cf/llava-hf/llava-1.5-7b-hf',
    available: false,
  },
  {
    id: 'imageClassification',
    label: 'Image Classification',
    description: 'Classifies the primary subject of an image.',
    category: 'Vision',
    model: '@cf/microsoft/resnet-50',
    available: false,
  },
  {
    id: 'objectDetection',
    label: 'Object Detection',
    description: 'Detects and localizes objects within an image.',
    category: 'Vision',
    model: '@cf/facebook/detr-resnet-50',
    available: false,
  },
  {
    id: 'imageGeneration',
    label: 'Image Generation',
    description: 'Generates a new image from a text prompt.',
    category: 'Image',
    model: 'deapi.ai Flux1schnell (text-to-image)',
    available: true,
  },
  {
    id: 'videoGeneration',
    label: 'Video Generation',
    description: 'Generates a short video from a text prompt.',
    category: 'Image',
    model: 'deapi.ai Ltxv_13B_0_9_8_Distilled_FP8 (text-to-video)',
    available: true,
  },
  {
    id: 'speechToText',
    label: 'Speech-to-Text',
    description: 'Transcribes speech from an audio attachment.',
    category: 'Audio',
    model: 'Deepgram nova-3',
    available: true,
  },
  {
    id: 'textToSpeech',
    label: 'Text-to-Speech',
    description: "Converts the agent's text replies into spoken audio.",
    category: 'Audio',
    model: '@cf/myshell-ai/melotts',
    available: false,
  },
];

export const ALWAYS_ON_CAPABILITY_ID = 'textChat';

/** Legacy agents (created before enabledCapabilities existed) default to this set. */
export const DEFAULT_ENABLED_CAPABILITIES = ['textChat', 'visionUnderstanding', 'ocr'];

export const AVAILABLE_CAPABILITY_IDS = new Set(
  AGENT_CAPABILITIES.filter((c) => c.available).map((c) => c.id)
);

/**
 * Parses a stored `enabledCapabilities` comma-separated string into a clean
 * Set of valid, available capability IDs. Defensively drops any ID that
 * isn't in the catalog or that maps to a currently-unavailable (blocked)
 * capability — those should never have been selectable, but a stale/tampered
 * doc shouldn't be able to unlock a blocked model call.
 *
 * Legacy agents (missing/empty enabledCapabilities) fall back to
 * DEFAULT_ENABLED_CAPABILITIES so existing agents keep working.
 */
export function parseEnabledCapabilities(raw: string | undefined | null): Set<string> {
  const trimmed = (raw ?? '').trim();
  const ids = trimmed
    ? trimmed.split(',').map((s) => s.trim()).filter(Boolean)
    : DEFAULT_ENABLED_CAPABILITIES;

  const filtered = ids.filter((id) => AVAILABLE_CAPABILITY_IDS.has(id));
  const result = new Set(filtered);
  result.add(ALWAYS_ON_CAPABILITY_ID); // textChat is always on, non-deselectable
  return result;
}

/** Serializes a set/array of capability IDs into the comma-separated storage format. */
export function serializeEnabledCapabilities(ids: Iterable<string>): string {
  const unique = Array.from(new Set(ids));
  if (!unique.includes(ALWAYS_ON_CAPABILITY_ID)) unique.unshift(ALWAYS_ON_CAPABILITY_ID);
  return unique.join(',');
}
